/**
 * Formulario de cuenta cliente (ADS-0121).
 *
 * La ficha de un cliente se abre por el id de su cuenta (`client_accounts`),
 * que no existe en `users`: por eso la edición carga y guarda contra
 * `/client-account/...` y no contra el endpoint de personas. Aquí viven las
 * dos traducciones entre la cuenta y los campos del formulario, que sigue
 * nombrando en castellano los datos del administrador.
 */

/** Campos del representante legal, tal como los nombra la API. */
export const LEGAL_REP_FIELDS = ['legal_rep_name', 'legal_rep_surname', 'legal_rep_document']

/** Campo del formulario => campo de la cuenta en la API. */
const FORM_TO_ACCOUNT = {
  nombreCuenta: 'username',
  cifCuenta: 'cif',
  mobile: 'mobile',
  direccion: 'address',
  codigoPostal: 'postal_code',
  municipio: 'city',
  provincia: 'state',
  observaciones: 'observations',
  comercial: 'commercial',
  father: 'commercial_user_id',
  estadoUsuario: 'status',
  notificaciones: 'has_notifications',
  maintenance: 'maintenance',
  contratoDatos: 'contract_data',
  repLegalNombre: 'legal_rep_name',
  repLegalApellidos: 'legal_rep_surname',
  repLegalDocumento: 'legal_rep_document',
}

const BOOLEAN_ACCOUNT_FIELDS = ['has_notifications', 'maintenance', 'contract_data']
const NUMERIC_ACCOUNT_FIELDS = ['commercial_user_id']

const text = (value) => (value === null || value === undefined ? '' : String(value))

/** El tipo de cuenta se guarda como filas en client_account_types. */
export const accountRole = (account) => {
  const types = (account?.types || []).map((type) => type?.type ?? type)
  const horizontal = types.includes('horizontal')
  const vertical = types.includes('vertical')
  if (horizontal && vertical) return 'both'
  if (horizontal) return 'horizontal'
  if (vertical) return 'vertical'
  return ''
}

/** Valores del formulario a partir de la cuenta que devuelve la API. */
export const accountToForm = (account) => ({
  nombreCuenta: text(account?.name),
  cifCuenta: text(account?.cif),
  mobile: text(account?.mobile),
  direccion: text(account?.address),
  codigoPostal: text(account?.postal_code),
  municipio: text(account?.city),
  provincia: text(account?.state),
  observaciones: text(account?.observations),
  comercial: text(account?.commercial_label ?? account?.commercial),
  father: account?.commercial_user_id ?? null,
  estadoUsuario: text(account?.status),
  notificaciones: Boolean(account?.has_notifications),
  maintenance: Boolean(account?.maintenance),
  contratoDatos: Boolean(account?.contract_data),
  tipoCuenta: accountRole(account),
  repLegalNombre: text(account?.legal_rep_name),
  repLegalApellidos: text(account?.legal_rep_surname),
  repLegalDocumento: text(account?.legal_rep_document),
})

/**
 * Cambios a enviar a `/client-account/update/{id}`: solo lo que difiere de la
 * cuenta cargada, para no pisar datos que el formulario no muestra. Un campo
 * vaciado viaja como null y así se puede borrar.
 */
export const accountChanges = (form, account) => {
  const original = accountToForm(account)
  const changes = {}

  for (const [field, apiField] of Object.entries(FORM_TO_ACCOUNT)) {
    const current = form?.[field]
    if (BOOLEAN_ACCOUNT_FIELDS.includes(apiField)) {
      if (Boolean(current) !== Boolean(original[field])) changes[apiField] = Boolean(current)
      continue
    }
    if (NUMERIC_ACCOUNT_FIELDS.includes(apiField)) {
      const value = current === '' || current === null || current === undefined ? null : Number(current)
      const previous = original[field] === null || original[field] === undefined ? null : Number(original[field])
      if (value !== previous) changes[apiField] = value
      continue
    }
    const value = text(current).trim()
    if (value !== text(original[field]).trim()) changes[apiField] = value === '' ? null : value
  }

  if (form?.tipoCuenta && form.tipoCuenta !== original.tipoCuenta) changes.role = form.tipoCuenta

  return changes
}

/** Datos del representante legal para el alta, que va por `/admin/create`. */
export const legalRepPayload = (form) => ({
  legal_rep_name: text(form?.repLegalNombre).trim() || null,
  legal_rep_surname: text(form?.repLegalApellidos).trim() || null,
  legal_rep_document: text(form?.repLegalDocumento).trim() || null,
})
